import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { brand, siteLogoUrlOnDark } from '../theme';

export default function Hero({
  title,
  subtitle,
  primaryCta = { label: 'Meet Concierge', to: '/concierge' },
  secondaryCta = { label: 'Talk to us', to: '/contact' },
  showLogo = true,
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: brand.navy,
        color: '#fff',
        pt: 0,
        pb: { xs: 12, md: 18 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1100px 520px at 80% -10%, rgba(30,91,255,0.45), transparent 60%),' +
            'radial-gradient(700px 380px at 0% 100%, rgba(201,161,74,0.18), transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.07,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(transparent 95%, rgba(255,255,255,1) 95%), linear-gradient(90deg, transparent 95%, rgba(255,255,255,1) 95%)',
          backgroundSize: '52px 52px',
          maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 80%)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Stack direction="column" spacing={0} alignItems="center">
          {showLogo && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: 680, sm: 840, md: 920 },
                display: 'flex',
                justifyContent: 'center',
                my: 0,
                py: 0,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  m: 0,
                  p: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={siteLogoUrlOnDark}
                  alt="G&R Nexus"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 22px 40px rgba(0,0,0,0.45))',
                  }}
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              width: '100%',
              maxWidth: 800,
              textAlign: 'center',
              ...(showLogo ? { mt: { xs: 0, md: '-40px' } } : { mt: 0 }),
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem', lg: '4rem' },
                color: '#fff',
                mt: 0,
                mb: 2.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                color: '#CBD5E1',
                maxWidth: 580,
                mx: 'auto',
                mb: 4,
              }}
            >
              {subtitle}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              {primaryCta && (
                <Button
                  component={RouterLink}
                  to={primaryCta.to}
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ px: 3.5, py: 1.4, fontSize: '1rem' }}
                >
                  {primaryCta.label}
                </Button>
              )}
              {secondaryCta && (
                <Button
                  component={RouterLink}
                  to={secondaryCta.to}
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 3.5,
                    py: 1.4,
                    fontSize: '1rem',
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.35)',
                    '&:hover': {
                      borderColor: '#fff',
                      bgcolor: 'rgba(255,255,255,0.06)',
                    },
                  }}
                >
                  {secondaryCta.label}
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
